import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Image as ImageIcon, Pencil, Plus } from 'lucide-react';
import sliderService, {
  LoadedSlider,
  PublicSlide,
  PublicSponsor,
  autoSlideMs,
  buildSlideImageUrl,
  isExternalLink,
  parseAspectRatio,
  slideImageFor,
  toInternalPath,
  visibleSlides,
} from '../../services/sliderService';
import sliderTracking, { SliderRef } from '../../services/sliderTracking';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import { loadSliderLocationName } from './sliderLocationNames';
import { t as tr } from '../../i18n';

const DESKTOP_QUERY = '(min-width: 1024px)';

/** Overlay „Slider: <Name>“ nur für Admins – lazy, damit normale Nutzer nichts davon laden */
const SliderEditorOverlay = React.lazy(() => import('./SliderEditorOverlay'));

/** Desktop-Erkennung (Tailwind lg) – reagiert auf Resize. */
function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState<boolean>(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(DESKTOP_QUERY).matches : true,
  );
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(DESKTOP_QUERY);
    const onChange = () => setIsDesktop(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  return isDesktop;
}

/** Light-Theme-Erkennung über `html.light` (Theme-Toggle der Web-App). */
function useIsLightTheme(): boolean {
  const read = () => typeof document !== 'undefined' && document.documentElement.classList.contains('light');
  const [isLight, setIsLight] = useState<boolean>(read);
  useEffect(() => {
    if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return;
    const obs = new MutationObserver(() => setIsLight(read()));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  return isLight;
}

/**
 * Sichtbarkeit ≥ 50 % (IntersectionObserver) für Impressionen. Fail-soft: ohne Observer
 * (alte Browser, jsdom) gilt der Slider ab dem ersten Anzeigen als sichtbar.
 */
function useHalfVisible(ref: React.RefObject<HTMLElement | null>, enabled: boolean): boolean {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!enabled) { setVisible(false); return; }
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setVisible(true); return; }
    const obs = new IntersectionObserver(
      (entries) => setVisible(entries.some((e) => e.intersectionRatio >= 0.5)),
      { threshold: [0.5] },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref, enabled]);
  return visible;
}

const ACCENT = '#3fced1';

export interface DynamicSliderProps {
  /** Location aus dem Slider-CMS (gleiche Location = gleiche Stelle in Web und App) */
  location: string;
  className?: string;
  /** Fallback-Seitenverhältnis, wenn der Slide keins hat */
  fallbackRatio?: string;
  /** Meldet die Zahl nutzbarer Slides (0 = Slider rendert nichts bzw. nur den Admin-Platzhalter) – für Layout-Anpassungen im Parent */
  onSlidesChange?: (count: number) => void;
  /**
   * Vorschau-Modus (Admin-Slide-Formular, 2026-09-27): dieser Slide wird statt der geladenen
   * Slides gerendert – derselbe Renderer wie live, keine zweite Implementierung. Links sind inert.
   */
  previewSlide?: PublicSlide | null;
  /** Vorschau: Sponsoren für Sponsor-Slides */
  previewSponsors?: PublicSponsor[];
  /** Vorschau: Viewport erzwingen (Desktop ≥ 1024 px / Mobile) statt window.matchMedia */
  previewViewport?: 'desktop' | 'mobile';
  /** Vorschau: helles Theme erzwingen (zeigt das Light-Bild); undefined = aktuelles Theme */
  previewLight?: boolean;
}

/**
 * Erster Web-Renderer des Slider-CMS (2026-09-15). Lädt Slider + aktive Slides einer
 * Location, rendert NICHTS solange geladen wird oder nichts da ist (kein Layout-Sprung
 * für leere Werbeflächen). Manual-Slides: Bild im Slide-Seitenverhältnis, optional
 * Titel/Untertitel/Button-Overlay, ganzer Slide verlinkt. Sponsor-Slides: Logos auf Weiß.
 * Auto-Slide nach autoSlideDuration (Default 5 s), Pause bei Hover, Pfeile + Punkte ab 2 Slides.
 *
 * 2026-09-28 (Marcel): Admins sehen für leere Locations IMMER einen 16:9-Platzhalter
 * (gestrichelt, Location-Name, „Slide hinzufügen“) und bei vorhandenen Slides ein Stift-Overlay;
 * beides öffnet das Inline-Overlay „Slider: <Name>“. Nicht-Admins sehen weiterhin nichts.
 * Tracking slider_impression (≥ 50 % sichtbar, 1× je Sitzung/Location/Slide) und slider_click –
 * nie für Admins, nie in der Vorschau.
 */
const DynamicSlider: React.FC<DynamicSliderProps> = ({
  location, className = '', fallbackRatio = '16:9', onSlidesChange,
  previewSlide = null, previewSponsors, previewViewport, previewLight,
}) => {
  // undefined = noch nicht geladen, null = geladen, aber nichts zu zeigen
  const [loadedData, setLoadedData] = useState<LoadedSlider | null | undefined>(undefined);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [locationName, setLocationName] = useState<string | null>(null);
  const notified = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const detectedDesktop = useIsDesktop();
  const detectedLight = useIsLightTheme();
  const isAdmin = useIsAdmin();
  const isPreview = previewSlide != null;
  const showAdmin = isAdmin && !isPreview;
  const isDesktop = previewViewport ? previewViewport === 'desktop' : detectedDesktop;
  const isLight = previewLight ?? detectedLight;

  const load = useCallback((force = false) => {
    let cancelled = false;
    sliderService.loadByLocation(location, { force }).then((loaded) => {
      if (cancelled) return;
      setLoadedData(loaded);
      setIndex(0);
    });
    return () => { cancelled = true; };
  }, [location]);

  useEffect(() => {
    if (isPreview) return; // Vorschau lädt nichts
    return load(false);
  }, [load, isPreview]);

  // Admin: Katalog-Name der Location für Platzhalter/Overlay-Titel (fail-soft → Schlüssel)
  useEffect(() => {
    if (!showAdmin) return;
    let cancelled = false;
    loadSliderLocationName(location).then((name) => { if (!cancelled) setLocationName(name); });
    return () => { cancelled = true; };
  }, [showAdmin, location]);

  const previewData = useMemo<LoadedSlider | null>(() => (previewSlide ? {
    slider: { id: 'preview', name: tr('web.component.dynamic-slider.slider'), location, active: true, order: 0, autoSlideDuration: null },
    slides: [previewSlide],
    sponsors: previewSponsors ?? [],
  } : null), [previewSlide, previewSponsors, location]);
  const data = isPreview ? previewData : loadedData;

  // 2026-09-15: desktopOnly-Slides nur auf Desktop; Zahl der sichtbaren Slides an den Parent melden
  const slides = data ? visibleSlides(data.slides, { isDesktop }) : [];
  const count = slides.length;
  useEffect(() => {
    if (data === undefined) return;
    if (notified.current !== count) {
      notified.current = count;
      onSlidesChange?.(count);
    }
    setIndex((i) => (count === 0 ? 0 : Math.min(i, count - 1)));
    // onSlidesChange bewusst nicht in den Deps (Parent-Callbacks sind oft nicht stabil)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, count]);
  const go = useCallback((next: number) => {
    if (count === 0) return;
    setIndex(((next % count) + count) % count);
  }, [count]);

  useEffect(() => {
    if (count < 2 || paused || editorOpen) return;
    const t = window.setInterval(() => go(index + 1), autoSlideMs(data?.slider.autoSlideDuration));
    return () => window.clearInterval(t);
  }, [count, paused, editorOpen, index, go, data?.slider.autoSlideDuration]);

  // ---- Tracking (nie Admin, nie Vorschau) ----
  const current: PublicSlide | undefined = count > 0 ? slides[Math.min(index, count - 1)] : undefined;
  const trackable = !isAdmin && !isPreview && count > 0;
  const halfVisible = useHalfVisible(containerRef, trackable);
  const trackingRef = useCallback((slide: PublicSlide): SliderRef => ({
    location,
    sliderId: data?.slider.id,
    slideId: slide.id,
    slideTitle: slide.title ?? null,
    slideType: slide.type,
  }), [location, data?.slider.id]);
  useEffect(() => {
    if (!trackable || !halfVisible || !current) return;
    sliderTracking.impression(trackingRef(current), { isAdmin, isPreview });
  }, [trackable, halfVisible, current, trackingRef, isAdmin, isPreview]);
  const onClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!trackable || !current) return;
    const target = e.target as Element | null;
    const anchor = target?.closest?.('a');
    if (!anchor || !containerRef.current?.contains(anchor)) return;
    sliderTracking.click(trackingRef(current), anchor.getAttribute('href'), { isAdmin, isPreview });
  };

  // ---- Admin: Overlay ----
  const displayName = locationName ?? location;
  const onEditorChanged = useCallback(() => {
    sliderService.clearCache();
    load(true);
  }, [load]);
  const editor = showAdmin && editorOpen ? (
    <Suspense fallback={null}>
      <SliderEditorOverlay location={location} name={displayName} onClose={() => setEditorOpen(false)} onChanged={onEditorChanged} />
    </Suspense>
  ) : null;

  if (data == null || count === 0) {
    // Noch nicht geladen → nichts (kein Sprung). Geladen und leer → nur Admins sehen den Platzhalter.
    if (data === undefined || !showAdmin) return null;
    return (
      <>
        <div
          className={`relative flex flex-col items-center justify-center gap-2 overflow-hidden rounded-2xl border border-dashed border-zinc-700 bg-zinc-900/60 p-4 text-center ${className}`}
          style={{ aspectRatio: '16 / 9' }}
          data-testid="dynamic-slider-placeholder"
          data-location={location}
          role="group"
          aria-label={tr('web.component.dynamic-slider.freie-werbeflaeche', { name: displayName })}
        >
          <ImageIcon size={22} className="text-zinc-500" aria-hidden />
          <p className="max-w-full truncate text-xs font-semibold text-zinc-300" title={location}>{displayName}</p>
          <button
            type="button"
            onClick={() => setEditorOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold text-black"
            style={{ backgroundColor: ACCENT }}
          >
            <Plus size={14} /> {tr('web.component.dynamic-slider.slide-hinzufuegen')}
          </button>
        </div>
        {editor}
      </>
    );
  }

  const shown = current!;
  const ratio = parseAspectRatio(shown.aspectRatio ?? fallbackRatio, parseAspectRatio(fallbackRatio));

  return (
    <>
    <div
      ref={containerRef}
      className={`relative overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 ${className}`}
      style={{ aspectRatio: String(ratio) }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onClickCapture={onClickCapture}
      role="region"
      aria-roledescription={tr('web.component.dynamic-slider.slider')}
      aria-label={data.slider.name}
      data-testid="dynamic-slider"
      data-preview={isPreview ? previewViewport ?? 'auto' : undefined}
    >
      {slides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-500 ${i === index ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
          aria-hidden={i !== index}
        >
          <SlideView slide={slide} sponsors={data.sponsors} isLight={isLight} inert={isPreview} />
        </div>
      ))}

      {count > 1 && (
        <>
          <button type="button" aria-label={tr('web.component.dynamic-slider.vorheriger-slide')} onClick={() => go(index - 1)}
            className="absolute left-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70">
            <ChevronLeft size={18} />
          </button>
          <button type="button" aria-label={tr('web.component.dynamic-slider.naechster-slide')} onClick={() => go(index + 1)}
            className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70">
            <ChevronRight size={18} />
          </button>
          <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5">
            {slides.map((s, i) => (
              <button key={s.id} type="button" aria-label={tr('web.component.dynamic-slider.slide', { i: i + 1 })} onClick={() => go(i)}
                className={`h-1.5 rounded-full transition-all ${i === index ? 'w-4' : 'w-1.5 bg-white/50'}`}
                style={i === index ? { backgroundColor: ACCENT } : undefined} />
            ))}
          </div>
        </>
      )}

      {showAdmin && (
        <button
          type="button"
          onClick={() => setEditorOpen(true)}
          aria-label={tr('web.component.dynamic-slider.slider-bearbeiten')}
          title={tr('web.component.dynamic-slider.slider-bearbeiten')}
          data-testid="dynamic-slider-edit"
          className="absolute right-2 top-2 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black/70 hover:bg-black/90"
        >
          <Pencil size={14} color={ACCENT} />
        </button>
      )}
    </div>
    {editor}
    </>
  );
};

const LinkWrap: React.FC<{ href?: string | null; label: string; inert?: boolean; children: React.ReactNode }> = ({ href, label, inert, children }) => {
  const url = (href ?? '').trim();
  // Vorschau: kein Navigieren aus dem Admin-Formular heraus (optisch identisch)
  if (!url || inert) return <div className="block h-full w-full" aria-label={label}>{children}</div>;
  if (isExternalLink(url)) {
    return <a href={url} target="_blank" rel="noopener noreferrer sponsored" aria-label={label} className="block h-full w-full">{children}</a>;
  }
  return <Link to={toInternalPath(url)} aria-label={label} className="block h-full w-full">{children}</Link>;
};

const SlideView: React.FC<{ slide: PublicSlide; sponsors: LoadedSlider['sponsors']; isLight: boolean; inert?: boolean }> = ({ slide, sponsors, isLight, inert }) => {
  if (slide.type === 'sponsor') {
    const ids = new Set(slide.sponsorIds ?? []);
    const list = sponsors.filter((s) => ids.has(s.id) && s.active !== false);
    return (
      <div className="flex h-full w-full items-center justify-center bg-white p-4">
        <div className={`grid h-full w-full gap-4 ${list.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
          {list.map((s) => {
            const img = buildSlideImageUrl(s.image);
            const inner = img ? <img src={img} alt={s.name ?? tr('web.component.dynamic-slider.sponsor')} className="h-full w-full object-contain" loading="lazy" />
              : <span className="text-sm font-bold text-zinc-800">{s.name}</span>;
            return (
              <div key={s.id} className="flex items-center justify-center">
                {s.url && !inert ? <a href={s.url} target="_blank" rel="noopener noreferrer sponsored" className="flex h-full w-full items-center justify-center">{inner}</a> : inner}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
  const img = buildSlideImageUrl(slideImageFor(slide, isLight));
  const hasText = Boolean(slide.title || slide.subtitle || slide.buttonText);
  return (
    <LinkWrap href={slide.linkUrl} label={slide.title || tr('web.component.dynamic-slider.slide-oeffnen')} inert={inert}>
      {img && <img src={img} alt={slide.title || ''} className="h-full w-full object-cover" loading="lazy" />}
      {hasText && (
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4 pt-10">
          {slide.title && <p className="text-base font-extrabold leading-tight text-white">{slide.title}</p>}
          {slide.subtitle && <p className="mt-0.5 text-sm text-gray-200">{slide.subtitle}</p>}
          {slide.buttonText && (
            <span className="mt-2 inline-flex rounded-lg px-3 py-1.5 text-xs font-bold text-black" style={{ backgroundColor: ACCENT }}>{slide.buttonText}</span>
          )}
        </div>
      )}
    </LinkWrap>
  );
};

export default DynamicSlider;
